// src/pages/SharePage.js
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints, PENDING_SHARE_KEY } from '../services/api';
import '../styles/pages/Page.css';
import '../styles/pages/Auth.css';
import '../styles/pages/Explore.css';
import '../styles/components/StatePage.css';
import Mascot from '../components/Mascot';
import { getFriendlyError } from '../components/errorUtils';


const SharePage = () => {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  const { token: authToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null); // {id, name, owner_name, step_count}

  // Public preview first, so the page (and the login page after it) can say WHICH
  // guide this link is for. A recipient with ten guides cannot tell otherwise.
  useEffect(() => {
    if (!tokenFromUrl) return;
    fetch(endpoints.guides.sharePreview(tokenFromUrl))
      .then(r => (r.ok ? r.json() : null))
      .then(setPreview)
      .catch(() => {});
  }, [tokenFromUrl]);

  useEffect(() => {
    if (authLoading) return;

    if (!tokenFromUrl) {
      setStatus('error');
      setError(getFriendlyError('Invalid or missing share token.', 'Share Page'));
      return;
    }

    if (!authToken) {
      // Survives the whole detour: register -> verification email (often opened in
      // another tab) -> back. A ?redirect= param does not, because the verify link
      // is minted server-side.
      localStorage.setItem(PENDING_SHARE_KEY, tokenFromUrl);
      setStatus('needs_login');
      return;
    }

    const claimAccess = async () => {
      try {
        const response = await fetch(endpoints.guides.claimAccess(tokenFromUrl), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Failed to claim access to the guide.');
        }

        const guide = await response.json();
        localStorage.removeItem(PENDING_SHARE_KEY);
        setStatus('success');
        // Straight to the guide that was shared, not the full list — landing on
        // /my-guides is the same "which one was it?" confusion one step later.
        navigate(`/guides/${guide.id}`, { replace: true });
      } catch (err) {
        setStatus('error');
        setError(getFriendlyError(err.message, 'Share Page'));
      }
    };

    claimAccess();
  }, [tokenFromUrl, authToken, authLoading, navigate]);

  // Carries the guide name through login/signup so those pages can show it too.
  const loginHref = `/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}` +
    (preview?.name ? `&guide=${encodeURIComponent(preview.name)}` : '');

  return (
    <div className="state-page share-page">
      <div className="share-page__inner">
        <div className="state-say">
          <Mascot size={40} />
          <div className="state-say__bubble">
            <b>Michii says</b>
            <span>{status === 'error' ? 'This link didn’t open for you.' : 'Checking your access to this guide.'}</span>
          </div>
        </div>
        {preview && (
          <p className="share-page__preview">
            {preview.owner_name ? <b>{preview.owner_name}</b> : 'Someone'} shared{' '}
            <b>“{preview.name}”</b>
            {preview.step_count ? ` (${preview.step_count} steps)` : ''} with you
          </p>
        )}

        {status === 'processing' && (
          <>
            <h1 className="share-page__title">Opening share link…</h1>
            <p className="auth-wait share-page__desc">Please wait while we authorize your access.</p>
          </>
        )}

        {status === 'needs_login' && (
          <>
            <h1 className="share-page__title">Log in to open this guide</h1>
            <div className="share-page__actions">
              <Link to={loginHref} className="lp-btn">Log in to continue</Link>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <h1 className="share-page__title">Access granted</h1>
            <p className="auth-wait share-page__desc">Opening the guide…</p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 className="share-page__title">Access denied</h1>
            <p className="share-page__error" role="alert">{error}</p>
            <div className="share-page__actions">
              <Link to="/explore" className="lp-btn quiet">Explore public guides</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SharePage;
